import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { getQaSelector } from '../../../../utils/common'
import Button from '../../../Button'
import { PageEmptyState } from '../../../PageEmptyState'
import { getIsApplicantFieldMasked } from '../../../RentalPortalApplicant'
import { Table } from '../../../Table'
import { TablePagination } from '../../../TablePagination'
import Text from '../../../Text'
import { useApplicantProfileContext } from '../RentalPortalApplicantProfile.hooks'

import { ApplicantProfileEmailsComposeButton } from './ApplicantProfileEmailsComposeButton'
import { ApplicantProfileEmailsRow } from './ApplicantProfileEmailsRow'
import {
  APPLICANT_PROFILE_EMAILS_COLUMNS_CONFIG,
  APPLICANT_PROFILE_EMAILS_SKELETON_ROWS,
  QA_NAME_APPLICANT_PROFILE_EMAILS,
  QA_NAME_APPLICANT_PROFILE_EMAILS_RETRY,
  QA_NAME_APPLICANT_PROFILE_EMAILS_TABLE,
} from './RentalPortalApplicantProfileEmails.constants'
import { useApplicantProfileEmails } from './RentalPortalApplicantProfileEmails.hooks'
import {
  getApplicantProfileEmailsState,
  getIsApplicantProfileEmailsEmpty,
  getIsApplicantProfileEmailsError,
  getIsApplicantProfileEmailsTableShown,
} from './RentalPortalApplicantProfileEmails.utils'

import * as styles from './RentalPortalApplicantProfileEmails.module.css'

import EmailIcon from '../../../../icons/email.svg'
import RefreshIcon from '../../../../icons/refresh.svg'
import RentalPortalIcon from '../../../../icons/rental-portal.svg'

function RentalPortalApplicantProfileEmails() {
  const { t } = useTranslation()

  const { applicantData } = useApplicantProfileContext()

  const containerRef = useRef(null)

  const {
    handleRefresh,
    handleSetPagination,
    hasError,
    isInitiated,
    isLoading,
    items,
    pagination,
  } = useApplicantProfileEmails({
    applicantId: applicantData?.id,
    emailCount: applicantData?.counts?.emails,
    isIdentityMasked: getIsApplicantFieldMasked(applicantData?.identity),
  })

  const state = getApplicantProfileEmailsState({
    emails: items,
    hasError,
    isInitiated,
  })

  return (
    <div
      {...getQaSelector({ value: QA_NAME_APPLICANT_PROFILE_EMAILS })}
      className={styles.RentalPortalApplicantProfileEmails}
      ref={containerRef}
    >
      <ApplicantProfileEmailsComposeButton className={styles.composeButton} />
      {getIsApplicantProfileEmailsError(state) ? (
        <PageEmptyState EntityIcon={RentalPortalIcon}>
          <Text isSecondary>
            {t('RentalPortal.ApplicantProfileEmailsLoadErrorKey')}
          </Text>
          <Button.Flat
            beforeIconProps={{ Icon: RefreshIcon }}
            isLoading={isLoading}
            qaName={QA_NAME_APPLICANT_PROFILE_EMAILS_RETRY}
            text={t('RentalPortal.ListingRetryBtnKey')}
            onClick={handleRefresh}
          />
        </PageEmptyState>
      ) : null}
      {getIsApplicantProfileEmailsEmpty(state) ? (
        <PageEmptyState
          EntityIcon={EmailIcon}
          text={t('RentalPortal.ApplicantProfileEmailsEmptyKey')}
        />
      ) : null}
      {getIsApplicantProfileEmailsTableShown(state) ? (
        <>
          <Table
            TableRow={ApplicantProfileEmailsRow}
            actionsColumnWidth={0}
            className={styles.table}
            columns={APPLICANT_PROFILE_EMAILS_COLUMNS_CONFIG}
            containerRef={containerRef}
            context={QA_NAME_APPLICANT_PROFILE_EMAILS_TABLE}
            headerRowProps={{ className: styles.row, resizable: false }}
            isLoading={isLoading}
            items={items}
            skeletonProps={{ withCheckbox: false }}
            skeletonRows={APPLICANT_PROFILE_EMAILS_SKELETON_ROWS}
          />
          <TablePagination
            responsive
            containerRef={containerRef}
            isSkeleton={!isInitiated}
            value={pagination}
            onChange={handleSetPagination}
          />
        </>
      ) : null}
    </div>
  )
}

export default RentalPortalApplicantProfileEmails
